import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import {
  allCategories,
  defaultFilters,
  filterComics,
  latestComics,
  searchComics,
} from "@/utils/comics";

const localPopularKeywords = ["热血", "恋爱", "奇幻", "悬疑", "校园", "古风", "科幻", "都市"];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [categoryId, setCategoryId] = useState("all");

  useEffect(() => {
    setKeyword(query);
    setCategoryId("all");
    setFilters({ ...defaultFilters });
  }, [query]);

  const matched = useMemo(() => (query.trim() ? searchComics(query) : []), [query]);
  const results = useMemo(
    () => filterComics(matched, filters, categoryId),
    [matched, filters, categoryId]
  );

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const word = keyword.trim();
    navigate(word ? `/search?q=${encodeURIComponent(word)}` : "/search");
  };

  const suggestions = useMemo(() => latestComics(4), []);
  const hasQuery = query.trim().length > 0;
  const resetAll = () => {
    setFilters({ ...defaultFilters });
    setCategoryId("all");
  };

  return (
    <SiteLayout>
      <PageMeta
        title={
          hasQuery
            ? `${query} 的搜索结果 - JMComic漫画频道`
            : "搜索漫画 - JMComic漫画频道 | 精选漫画阅读平台"
        }
        description={
          hasQuery
            ? `JMComic中与「${query}」相关的漫画搜索结果，支持按题材与连载状态进一步筛选。`
            : "在JMComic搜索漫画、作者或题材，快速找到想看的精选漫画作品。"
        }
        keywords="JMComic,漫画搜索,漫画查找,连载漫画"
        path="/search"
      />

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-9">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">搜索漫画</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
            搜索漫画
          </h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            输入漫画名、作者名或题材关键词，支持标题、作者、简介与标签的匹配。
          </p>

          <form onSubmit={submitSearch} className="mt-5 flex max-w-2xl items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="例如：热血、恋爱、苍蓝之刃"
                aria-label="搜索关键词"
                className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索：</span>
            {localPopularKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => navigate(`/search?q=${encodeURIComponent(word)}`)}
                className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
        {hasQuery ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                关键词「<strong className="font-semibold text-primary-700">{query}</strong>」共找到{" "}
                <strong className="font-heading text-base text-foreground-950">{results.length}</strong> 部漫画
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-foreground-400">排序：{filters.sort}</span>
                {(filters.tag !== "全部" || filters.region !== "全部" || filters.status !== "全部" || categoryId !== "all") && (
                  <button
                    type="button"
                    onClick={resetAll}
                    className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-[11px] text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-refresh-line"></i>
                    清除筛选
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-12 shrink-0 text-[11px] font-medium text-foreground-400">类型</span>
                <button
                  type="button"
                  onClick={() => setCategoryId("all")}
                  className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    categoryId === "all"
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  全部
                </button>
                {allCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setCategoryId(category.id)}
                    className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      categoryId === category.id
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-12 shrink-0 text-[11px] font-medium text-foreground-400">状态</span>
                {["全部", "连载", "完结"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setFilters({ ...filters, status })}
                    className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      filters.status === status
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-12 shrink-0 text-[11px] font-medium text-foreground-400">排序</span>
                {["更新时间", "热度优先", "收藏优先"].map((sort) => (
                  <button
                    key={sort}
                    type="button"
                    onClick={() => setFilters({ ...filters, sort })}
                    className={`whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      filters.sort === sort
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {sort}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                {results.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的漫画
                </h2>
                <p className="mt-1.5 max-w-md text-sm leading-relaxed text-foreground-500">
                  试试更换关键词，或减少筛选条件。也可以从热门题材里挑一个方向继续浏览。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={resetAll}
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-refresh-line"></i>
                    清除筛选
                  </button>
                  <Link
                    to="/"
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}

            <section className="mt-12">
              <h2 className="font-heading text-lg font-bold text-foreground-950">换个方向看看</h2>
              <p className="mt-1 text-xs text-foreground-500">近期更新较多的作品，也许正合你口味。</p>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                {suggestions.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-14 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-search-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  输入关键词开始搜索
                </h2>
                <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-foreground-500">
                  可以搜索漫画名、作者名、题材标签，也可以直接点击上方热门搜索快速进入。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {localPopularKeywords.slice(0, 4).map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => navigate(`/search?q=${encodeURIComponent(word)}`)}
                      className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 hover:bg-secondary-200"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">最近更新</h2>
                <ul className="mt-3 space-y-2">
                  {suggestions.map((comic) => (
                    <li key={comic.id}>
                      <Link
                        to={`/detail/${comic.id}`}
                        className="flex items-center gap-2.5 rounded-md p-1.5 transition-colors hover:bg-background-50"
                      >
                        <span className="h-12 w-9 shrink-0 overflow-hidden rounded bg-secondary-100">
                          <img
                            src={comic.cover}
                            alt={`${comic.title}封面`}
                            title={`${comic.title} - JMComic漫画`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-semibold text-foreground-900">
                            {comic.title}
                          </span>
                          <span className="block truncate text-[10px] text-foreground-400">
                            {comic.latestChapter}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-300"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}